import { CountryCode } from '../enum/country-code'
import { Category } from '../enum/site-category'
import {
  enumWithFallback,
  objectSchema,
  optionalTolerant,
  requiredTolerant,
  tolerantArray,
} from '../../_utils/fcdc-v2'
import { contactPersonSchema } from './contact-person.schema'
import { i18nStringSchema } from './i18n-string.schema'
import { SettingsSchema } from './settings.schema'
import * as z from 'zod'

const namesSchema = optionalTolerant(tolerantArray(i18nStringSchema))
const settingsSchema = optionalTolerant(SettingsSchema)
const dateSchema = requiredTolerant(z.union([z.string(), z.number()]))
const textSchema = requiredTolerant(z.string())

function enumValues<T extends Record<string, string>>(
  enumObj: T
): [string, ...string[]] {
  const values = [...new Set(Object.values(enumObj))]
  return values as [string, ...string[]]
}

const countryCodeSchema = requiredTolerant(z.enum(enumValues(CountryCode)))

export const coordinatesSchema = objectSchema({
  latitude: requiredTolerant(z.number()),
  longitude: requiredTolerant(z.number()),
})

export const CountrySchema = objectSchema({
  id: countryCodeSchema,
  settings: settingsSchema,
  names: namesSchema,
  deleted: requiredTolerant(z.boolean()),
  created: dateSchema,
  modified: dateSchema,
})

export const CustomerSchema = objectSchema({
  id: textSchema,
  settings: settingsSchema,
  names: namesSchema,
  deleted: requiredTolerant(z.boolean()),
  created: dateSchema,
  modified: dateSchema,
})

export const PresenceSchema = objectSchema({
  id: textSchema,
  customerId: textSchema,
  countryCode: countryCodeSchema,
  settings: settingsSchema,
  names: namesSchema,
  deleted: requiredTolerant(z.boolean()),
  created: dateSchema,
  modified: dateSchema,
})

export const LocationSchema = objectSchema({
  id: textSchema,
  siteId: textSchema,
  settings: settingsSchema,
  names: namesSchema,
  coordinates: optionalTolerant(coordinatesSchema),
  deleted: requiredTolerant(z.boolean()),
  created: dateSchema,
  modified: dateSchema,
})

export const SiteSchema = objectSchema({
  id: textSchema,
  customerId: textSchema,
  presenceId: textSchema,
  pollable: requiredTolerant(z.boolean()),
  settings: settingsSchema,
  category: enumWithFallback(Category, Category.Unspecified),
  names: namesSchema,
  countryCode: countryCodeSchema,
  province: optionalTolerant(z.string()),
  postcode: textSchema,
  city: textSchema,
  address: textSchema,
  timezone: textSchema,
  serviceRepresentative: optionalTolerant(contactPersonSchema),
  technician: optionalTolerant(contactPersonSchema),
  contactPersons: optionalTolerant(tolerantArray(contactPersonSchema)),
  watchdogRecipients: optionalTolerant(tolerantArray(z.unknown())),
  watchdogSnapshot: optionalTolerant(z.unknown()),
  supportInfos: optionalTolerant(tolerantArray(i18nStringSchema)),
  deleted: requiredTolerant(z.boolean()),
  created: dateSchema,
  modified: dateSchema,
})
